import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Empty, ErrorBox, PageHead, ResultBadge } from "../components/Bits";
import { Badge, Card, LinkButton, Select, Table } from "../design-system/components";
import { endpoints } from "../lib/api";
import { END_REASON_LABEL, formatDateTime } from "../lib/format";

export function GamesPage() {
  const games = useQuery({ queryKey: ["games"], queryFn: endpoints.games });
  const decks = useQuery({ queryKey: ["decks"], queryFn: endpoints.decks });
  const [deckFilter, setDeckFilter] = useState("");
  const [resultFilter, setResultFilter] = useState("");
  const rows = useMemo(
    () =>
      (games.data?.games ?? []).filter(
        (g) =>
          (deckFilter === "" || g.deckId === deckFilter) &&
          (resultFilter === "" || g.result === resultFilter),
      ),
    [games.data, deckFilter, resultFilter],
  );
  if (games.error) return <ErrorBox error={games.error} />;
  return (
    <div className="rk-stack">
      <PageHead
        title="Games"
        subtitle={`${games.data?.games.length ?? 0} uploaded`}
        actions={<LinkButton href="/upload">Upload a log</LinkButton>}
      />
      <div className="pr-inline">
        <div style={{ width: 240 }}>
          <Select
            size="sm"
            value={deckFilter}
            onChange={setDeckFilter}
            options={[
              { value: "", label: "All decks" },
              ...(decks.data?.decks ?? []).map((d) => ({ value: d.deckId, label: d.name })),
            ]}
          />
        </div>
        <div style={{ width: 160 }}>
          <Select
            size="sm"
            value={resultFilter}
            onChange={setResultFilter}
            options={[
              { value: "", label: "All results" },
              { value: "win", label: "Wins" },
              { value: "loss", label: "Losses" },
              { value: "unknown", label: "Unknown" },
            ]}
          />
        </div>
      </div>
      <Card flush>
        {rows.length === 0 ? (
          <Empty title="No games match" />
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Played</th>
                <th>Opponent</th>
                <th>Deck</th>
                <th>Result</th>
                <th>Ended</th>
                <th className="num">Turns</th>
                <th className="num">Prizes</th>
                <th>Order</th>
                <th>Season</th>
                <th className="num">Elo</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.gameId}>
                  <td>
                    <Link to={`/games/${g.gameId}`}>{formatDateTime(g.playedAt)}</Link>
                  </td>
                  <td>
                    {g.opponentName ?? <span className="pr-muted">unknown side</span>}
                    {g.opponentArchetype ? (
                      <>
                        {" "}
                        <Badge variant="outline">{g.opponentArchetype}</Badge>
                      </>
                    ) : null}
                  </td>
                  <td>
                    {g.deckId ? (
                      <Link to={`/decks/${g.deckId}`}>{g.deckName ?? "Deck"}</Link>
                    ) : (
                      <span className="pr-muted">—</span>
                    )}
                  </td>
                  <td>
                    <ResultBadge game={g} />
                  </td>
                  <td className="pr-muted">{END_REASON_LABEL[g.endReason] ?? g.endReason}</td>
                  <td className="num">{g.turnCount}</td>
                  <td className="num">
                    {g.stats.me?.prizesTaken ?? "—"}–{g.stats.opponent?.prizesTaken ?? "—"}
                  </td>
                  <td className="pr-muted">
                    {g.wentFirst === undefined ? "—" : g.wentFirst ? "1st" : "2nd"}
                  </td>
                  <td className="pr-muted">{g.seasonName ?? g.seasonId ?? "—"}</td>
                  <td className="num">
                    {g.elo ? (
                      <>
                        {g.elo.newElo}{" "}
                        <span
                          style={{
                            color: g.elo.delta >= 0 ? "var(--pr-success)" : "var(--pr-error)",
                          }}
                        >
                          ({g.elo.delta >= 0 ? "+" : ""}
                          {g.elo.delta})
                        </span>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <Badge variant={g.exportVariant === "debug" ? "accent" : "outline"}>
                      {g.exportVariant}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
